/**
 * LA PAPELERA DE UN PRESUPUESTO (administración, 23/10/2026)
 * ---------------------------------------------------------------------------
 * «Desde el apartado de "Presupuestos" en la ficha general del paciente, debe
 * ser posible borrar presupuestos que se hayan generado para ese paciente».
 *
 * La misma en las dos pantallas —la pestaña «Presupuestos» de la ficha y la
 * lista de Presupuestos—, con la misma regla y el mismo motivo: los decide el
 * SERVIDOR (backend/services/borrar_presupuesto.py) y llegan en cada
 * presupuesto como `no_se_borra` (el texto, o null si se puede) y
 * `al_borrar` (qué pasaría con sus enlaces de WhatsApp). Aquí no se vuelve a
 * calcular nada: lo que impide borrar —un cobro, la reserva de una cita— no
 * está en esta pantalla.
 *
 *   · Se puede: la papelera pregunta antes «¿Borrar el presupuesto Nº X? No
 *     se puede deshacer» y dice, con los números, qué pasa con sus enlaces:
 *     el de su paquete seguirá enseñando los demás («seguirá enseñando
 *     PRES-2»), el suyo solo deja de enseñarlo, y de qué paquete de otro se
 *     quita. Al terminar, la lista se vuelve a pedir.
 *   · No se puede: el botón sale deshabilitado y el motivo sale al pasar el
 *     ratón (el `title` va en la envoltura, porque el botón deshabilitado no
 *     recibe el ratón) y lo lee el lector de pantalla con lo que es el botón
 *     («Borrar el presupuesto X. No se puede borrar: …»).
 *   · Si el servidor dice que no (409: se le acaba de enlazar un cobro, por
 *     ejemplo), el aviso dice su motivo y la lista se vuelve a pedir, para que
 *     la papelera salga ya deshabilitada con él.
 *   · Si se borró pero algo de después no salió (`a_medias`), se avisa de
 *     qué quedó a medias.
 *
 * Sin emojis: el icono de la papelera de lucide, como en el resto de la app.
 */
import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, mensajeError } from '../lib/api';
import { Button } from './ui/button';

/** «a», «a y b», «a, b y c». */
function juntar(partes) {
  const p = (partes || []).filter(Boolean);
  if (p.length <= 1) return p.join('');
  return `${p.slice(0, -1).join(', ')} y ${p[p.length - 1]}`;
}

/** ¿Se le mandó al paciente por WhatsApp (suelto o en un paquete)? */
function seLeMando(b) {
  return (b?.envios_publicos || []).length > 0 || !!b?.paquete_public_token || b?.status === 'sent';
}

/** El texto de la confirmación. `conPaciente`: la lista de Presupuestos, que
 *  es de toda la clínica, dice además de quién es. */
export function avisoAntesDeBorrar(b, conPaciente = false) {
  const de = conPaciente && b?.patient_name ? ` de ${b.patient_name}` : '';
  let aviso = `¿Borrar el presupuesto Nº ${b?.number || ''}${de}? No se puede deshacer.`;
  const al = b?.al_borrar;
  if (al) {
    if ((al.siguen || []).length) {
      aviso += `\n\nEl enlace de WhatsApp de este paquete seguirá enseñando ${juntar(al.siguen)}.`;
    }
    if (al.retira_enlace) {
      aviso += '\n\nSu enlace de WhatsApp dejará de enseñarlo: el paciente leerá que la clínica '
        + 'lo ha retirado.';
    }
    if ((al.quita_de || []).length) {
      aviso += `\n\nSe quita del paquete que se mandó con ${juntar(al.quita_de)}: `
        + 'ese enlace seguirá enseñando lo demás.';
    }
  } else if (seLeMando(b)) {
    aviso += '\n\nSi se le mandó por WhatsApp (suelto o en un paquete), su enlace deja de '
      + 'enseñarlo: el paciente leerá que la clínica lo ha retirado.';
  }
  return aviso;
}

/** Lo que dice el aviso de «borrado», con lo que pasó con sus enlaces. */
export function textoBorrado(number, enlaces) {
  const partes = [];
  if (enlaces?.retirados) partes.push('su enlace de WhatsApp ya no lo enseña');
  if ((enlaces?.siguen || []).length) {
    partes.push(`el enlace de su paquete sigue enseñando ${juntar(enlaces.siguen)}`);
  }
  if ((enlaces?.paquetes || []).length) partes.push(`quitado del paquete de ${juntar(enlaces.paquetes)}`);
  return `Presupuesto ${number} borrado${partes.length ? ` · ${partes.join(' · ')}` : ''}`;
}

export default function BotonBorrarPresupuesto({ budget, onBorrado, conPaciente = false, testId }) {
  const [borrando, setBorrando] = useState(false);
  const base = testId || 'presupuesto-borrar';
  const motivo = budget?.no_se_borra || null;

  if (motivo) {
    return (
      <span className="inline-flex" title={motivo} data-testid={`${base}-motivo-${budget.id}`}>
        <Button size="sm" variant="ghost" disabled
          aria-label={`Borrar el presupuesto ${budget?.number || ''}. ${motivo}`}
          className="text-muted-foreground" data-testid={`${base}-${budget.id}`}>
          <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
        </Button>
      </span>
    );
  }

  const borrar = async () => {
    if (borrando) return;
    if (!window.confirm(avisoAntesDeBorrar(budget, conPaciente))) return;
    setBorrando(true);
    try {
      const r = await api.delete(`/api/budgets/${budget.id}`);
      toast.success(textoBorrado(budget.number, r?.data?.enlaces));
      const aMedias = r?.data?.a_medias || [];
      if (aMedias.length) {
        toast.warning(`Borrado, pero ha quedado algo a medias: ${aMedias.join('; ')}.`);
      }
    } catch (e) {
      // El motivo lo escribe el servidor (p. ej. que se le acaba de enlazar un cobro).
      toast.error(mensajeError(e, 'No se ha podido borrar el presupuesto'));
    } finally {
      setBorrando(false);
      if (onBorrado) onBorrado();
    }
  };

  return (
    <Button size="sm" variant="ghost" onClick={borrar} disabled={borrando}
      className="text-destructive"
      title="Borrar este presupuesto"
      aria-label={`Borrar el presupuesto ${budget?.number || ''}`.trim()}
      data-testid={`${base}-${budget.id}`}>
      <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
    </Button>
  );
}
